import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import union1 from "../../assets/images/svg/union-1.svg";
import vector3 from "../../assets/images/svg/vector-3.svg";
import line3 from "../../assets/images/svg/line-5-3.svg";
import rectangle5 from "../../assets/images/png/rectangle-5-4.png";
import metaStep1 from "../../assets/images/svg/meta-step1.svg";
import createSmartLinkButton from "../../assets/images/svg/create-smart-link-button.svg";
import campaignSortIcon from "../../assets/images/svg/campaign-sort-icon.svg";
import agnleDownSmall from "../../assets/images/svg/angle-down-small-4.svg";
import brandLogo from "../../assets/images/svg/white-logo-new.svg";
import { ROUTES } from "../../components/consts/routes.consts";
import { apiBaseUrl } from "../../components/consts/api-url.const";
import MetaBuilderCampaignCard from "../../components/metaBuilder/MetaBuilderCampaignCard";
import SortSelector, { SORT_OPTIONS } from "../../components/SortSelector";
import DeleteConfirmationModal from "../../components/DeleteConfirmationModal";
import EditConfirmationModal from "../../components/EditConfirmationModal";
import MetaAdsHeaderNav from "../../components/metaAds/MetaAdsHeaderNav";
import {
  useGetMetaBuilderCampaignsFromMetaQuery,
  useUpdateMetaBuilderCampaignStatusMutation,
  useDuplicateMetaBuilderCampaignMutation,
  useGetMetaBuilderAuthStatusQuery,
  useMetaBuilderRefreshMutation,
  useGetDraftCampaignsQuery,
  useDeleteMetaBuilderCampaignMutation,
  useDeleteDraftCampaignMutation,
  useGetMetaBuilderCampaignFromMetaQuery,
  useLazyGetVideosByIdsQuery,
  useSaveDraftCampaignMutation,
  useGetMetaBuilderAdAccountsQuery,
  MetaCampaignFromMeta,
  DraftCampaign,
} from "../../components/apis/metaBuilderApi";
import { CampaignCardData } from "../../utils/campaignTransform.util";

type AdAccountOption = {
  id?: string;
  account_id?: string;
  name?: string;
};

const AD_ACCOUNT_STORAGE_KEY = "metaAdsSelectedAdAccount";

const inferPromoteTypeFromUrl = (url?: string | null): "track" | "playlist" => {
  if (url && typeof url === "string" && url.includes("/playlist/")) {
    return "playlist";
  }
  return "track";
};

const getRtkQueryStatus = (err: unknown): number | undefined => {
  if (!err || typeof err !== "object") return undefined;
  const maybe = err as { status?: unknown };
  return typeof maybe.status === "number" ? maybe.status : undefined;
};

const isAuthLikeError = (err: unknown): boolean => {
  const status = getRtkQueryStatus(err);
  return status === 401 || status === 403;
};
// Helper function to transform Meta campaign to CampaignCardData
const transformMetaCampaignFromMeta = (
  metaCampaign: MetaCampaignFromMeta,
): CampaignCardData => {
  // Format status
  const status =
    metaCampaign.status === "ACTIVE"
      ? ("Active" as const)
      : metaCampaign.status === "PAUSED"
        ? ("Paused" as const)
        : ("Active" as const);

  // Format dates
  const formatDate = (dateStr?: string | null) => {
    if (!dateStr) return "None";
    return dayjs(dateStr).format("DD MMM YYYY");
  };

  // Calculate budget string
  const getBudgetString = () => {
    // Convert string/number to number and handle both types
    const dailyBudget = metaCampaign.daily_budget
      ? (typeof metaCampaign.daily_budget === "string"
          ? parseFloat(metaCampaign.daily_budget)
          : metaCampaign.daily_budget) / 100
      : undefined;
    const lifetimeBudget = metaCampaign.lifetime_budget
      ? (typeof metaCampaign.lifetime_budget === "string"
          ? parseFloat(metaCampaign.lifetime_budget)
          : metaCampaign.lifetime_budget) / 100
      : undefined;

    // Check if campaign has an end date
    const hasEndDate = metaCampaign.stop_time && metaCampaign.stop_time !== "";

    // Use daily_budget if available, otherwise use lifetime_budget
    const dailyAmount =
      dailyBudget || (lifetimeBudget ? lifetimeBudget / 1 : undefined);

    if (dailyAmount) {
      if (hasEndDate) {
        // Calculate total budget: daily × days
        const startDate = dayjs(metaCampaign.start_time);
        const endDate = dayjs(metaCampaign.stop_time);
        // Calculate days difference using ceil to include partial days
        const days = Math.ceil(endDate.diff(startDate, "hour") / 24);

        if (days > 0) {
          const total = dailyAmount * days;
          return `$${total.toFixed(2)} ($${dailyAmount.toFixed(2)}/day)`;
        }
      }
      // No end date - show daily budget only
      return `$${dailyAmount.toFixed(2)}/day`;
    }

    return "Not set";
  };

  // Format numbers with commas
  const formatNumber = (num: number) => {
    return num.toLocaleString();
  };

  // Format last updated - shows Today/Yesterday with exact time from API (no local conversion)
  const formatLastUpdatedExact = (dateStr?: string | null) => {
    if (!dateStr) return "N/A";

    // Parse the API date string and extract time directly from it
    // Format: "2026-01-17T20:08:11+0200" or similar ISO formats
    const apiDate = new Date(dateStr);

    // Extract time directly from the parsed date (hours and minutes)
    const hours = apiDate.getHours().toString().padStart(2, "0");
    const minutes = apiDate.getMinutes().toString().padStart(2, "0");
    const timeStr = `${hours}:${minutes}`;

    // Get date in UTC to avoid local timezone issues
    const apiDateUtc = Date.UTC(
      apiDate.getUTCFullYear(),
      apiDate.getUTCMonth(),
      apiDate.getUTCDate(),
    );

    // Get today's date in UTC
    const today = new Date();
    const todayUtc = Date.UTC(
      today.getUTCFullYear(),
      today.getUTCMonth(),
      today.getUTCDate(),
    );

    // Calculate difference in days
    const diffTime = todayUtc - apiDateUtc;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return `Today, ${timeStr}`;
    } else if (diffDays === 1) {
      return `Yesterday, ${timeStr}`;
    } else {
      // Show date with time from API
      const day = apiDate.getUTCDate().toString().padStart(2, "0");
      const month = (apiDate.getUTCMonth() + 1).toString().padStart(2, "0");
      const year = apiDate.getUTCFullYear();
      return `${day}/${month}/${year}, ${timeStr}`;
    }
  };

  return {
    id: metaCampaign.id,
    name: metaCampaign.name,
    status,
    conversionPage: "Meta Ads Campaign",
    startDate: formatDate(metaCampaign.start_time),
    endDate: formatDate(metaCampaign.stop_time),
    budget: getBudgetString(),
    engagement: "N/A", // Not available from Meta without insights call
    visits: "N/A",
    clicks: "N/A",
    cost: "N/A",
    results: {
      clicks: "N/A",
      ctr: "N/A",
      conversions: "N/A",
    },
    lastUpdated: formatLastUpdatedExact(metaCampaign.updated_time),
    isFromMeta: true,
    promoteType:
      metaCampaign.promoteType ||
      inferPromoteTypeFromUrl(metaCampaign.linkUrls?.[0]),
    objective: metaCampaign.objective,
    accountId: metaCampaign.account_id,
    createdAt: metaCampaign.created_time,
    updatedAt: metaCampaign.updated_time,
    linkUrls: metaCampaign.linkUrls || [], // Add linkUrls from Meta API response
    videos: metaCampaign.videos || [], // Add videos from Meta API response
    ...(metaCampaign.performance != null && {
      performance: metaCampaign.performance,
    }),
  };
};

// Helper function to transform Draft campaign to CampaignCardData
const transformDraftCampaign = (draft: DraftCampaign): CampaignCardData => {
  // Draft campaigns always show as "Draft" status
  const status = "Draft" as const;

  // Format dates
  const formatDate = (dateStr?: string | null) => {
    if (!dateStr) return "None";
    return dayjs(dateStr).format("DD MMM YYYY");
  };

  // Calculate budget string
  const getBudgetString = () => {
    const budget = draft.budget?.amount || 0;
    const budgetType = draft.budget?.type || "DAILY";
    const endDate = draft.schedule?.endDate;
    const startDate = draft.schedule?.startDate;

    if (!budget) return "Not set";

    // Check if campaign has an end date
    const hasEndDate = endDate && endDate !== "None" && endDate.trim() !== "";

    if (hasEndDate && startDate) {
      // Calculate total budget: daily × days
      const start = dayjs(startDate);
      const end = dayjs(endDate);
      const days = end.diff(start, "day") + 1; // Include both start and end days

      if (days > 0) {
        const total = budget * days;
        return `$${total.toFixed(2)} ($${budget.toFixed(2)}/day)`;
      }
    }

    // No end date or invalid dates - show daily budget only
    return `$${budget.toFixed(2)}/day`;
  };

  // Format last updated - shows Today/Yesterday with exact time from API (no local conversion)
  const formatLastUpdatedExact = (dateStr?: string) => {
    if (!dateStr) return "N/A";

    // Parse the API date string and extract time directly from it
    const apiDate = new Date(dateStr);

    // Extract time directly from the parsed date (hours and minutes)
    const hours = apiDate.getHours().toString().padStart(2, "0");
    const minutes = apiDate.getMinutes().toString().padStart(2, "0");
    const timeStr = `${hours}:${minutes}`;

    // Get date in UTC to avoid local timezone issues
    const apiDateUtc = Date.UTC(
      apiDate.getUTCFullYear(),
      apiDate.getUTCMonth(),
      apiDate.getUTCDate(),
    );

    // Get today's date in UTC
    const today = new Date();
    const todayUtc = Date.UTC(
      today.getUTCFullYear(),
      today.getUTCMonth(),
      today.getUTCDate(),
    );

    // Calculate difference in days
    const diffTime = todayUtc - apiDateUtc;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return `Today, ${timeStr}`;
    } else if (diffDays === 1) {
      return `Yesterday, ${timeStr}`;
    } else {
      // Show date with time from API
      const day = apiDate.getUTCDate().toString().padStart(2, "0");
      const month = (apiDate.getUTCMonth() + 1).toString().padStart(2, "0");
      const year = apiDate.getUTCFullYear();
      return `${day}/${month}/${year}, ${timeStr}`;
    }
  };

  return {
    id: draft._id,
    name: draft.campaignName,
    status,
    conversionPage: "Draft Campaign",
    startDate: formatDate(draft.schedule?.startDate),
    endDate: formatDate(draft.schedule?.endDate),
    budget: getBudgetString(),
    engagement: "N/A",
    visits: "N/A",
    clicks: "N/A",
    cost: "N/A",
    results: {
      clicks: "N/A",
      ctr: "N/A",
      conversions: "N/A",
    },
    lastUpdated: formatLastUpdatedExact(draft.updatedAt),
    isFromMeta: false,
    isDraft: true,
    promoteType:
      draft.metadata?.promoteType ||
      inferPromoteTypeFromUrl(draft.metadata?.spotifyUrl),
    objective: draft.objective,
    accountId: draft.adAccountId,
    createdAt: draft.createdAt,
    updatedAt: draft.updatedAt,
    linkUrls: draft.metadata?.spotifyUrl ? [draft.metadata.spotifyUrl] : [],
    videos:
      draft.s3Videos?.map((video: any) => ({
        id: video.key,
        title: video.fileName || `Video ${video.key?.substring(0, 8)}...`,
      })) || [],
  };
};

export default function CampaignList() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<"artists" | "curators">("artists");
  const [tracksTabState, setTracksTabState] = useState<"tracks" | "playlists">(
    "tracks",
  );

  // Keep audience tab (artists/curators) and promote type (tracks/playlists) in sync
  const handleAudienceTabChange = (tab: "artists" | "curators") => {
    setActiveTab(tab);
    setTracksTabState(tab === "curators" ? "playlists" : "tracks");
  };
  const handlePromoteTabChange = (tab: "tracks" | "playlists") => {
    setTracksTabState(tab);
    setActiveTab(tab === "playlists" ? "curators" : "artists");
  };

  const [searchQuery, setSearchQuery] = useState("");
  const [sortOption, setSortOption] = useState(SORT_OPTIONS[0]);
  const [hasShownError, setHasShownError] = useState(false);
  const [updatingCampaignId, setUpdatingCampaignId] = useState<string | null>(
    null,
  );
  const [duplicatingCampaignId, setDuplicatingCampaignId] = useState<
    string | null
  >(null);
  const [deletingCampaignId, setDeletingCampaignId] = useState<string | null>(
    null,
  );
  const [selectedPageId, setSelectedPageId] = useState<string | null>(null);
  const [selectedAdAccount, setSelectedAdAccount] =
    useState<AdAccountOption | null>(null);

  // Delete confirmation modal state
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    campaignId: string | number;
    campaignName: string;
    isDraft: boolean;
  }>({
    isOpen: false,
    campaignId: "",
    campaignName: "",
    isDraft: false,
  });

  // Edit confirmation modal state
  const [editModal, setEditModal] = useState<{
    isOpen: boolean;
    campaignId: string | number;
  }>({
    isOpen: false,
    campaignId: "",
  });

  // Check Facebook connection status
  const { data: authStatusData, isLoading: isAuthStatusLoading } =
    useGetMetaBuilderAuthStatusQuery();
  const isFacebookConnected = authStatusData?.connected ?? false;
  const [refreshMetaSession] = useMetaBuilderRefreshMutation();
  const [isRecoveringMeta, setIsRecoveringMeta] = useState(false);
  const [hasAutoRetriedMeta, setHasAutoRetriedMeta] = useState(false);

  // Restore selections (pageId must always come from localStorage.metaAdsAccountData)
  const [adAccountId, setAdAccountId] = useState<string | null>(null);
  useEffect(() => {
    try {
      const accountData = localStorage.getItem("metaAdsAccountData");
      if (!accountData) return;

      const parsedData = JSON.parse(accountData);

      const storedPageId: string | null = parsedData?.page?.id || null;
      if (storedPageId) setSelectedPageId(storedPageId);

      const storedAdAccountId: string | null =
        parsedData?.adAccount?.id || parsedData?.adAccount?.account_id || null;
      if (storedAdAccountId) {
        setAdAccountId(storedAdAccountId);
        setSelectedAdAccount(parsedData.adAccount);
      }
    } catch (e) {
      console.error("Failed to parse metaAdsAccountData:", e);
    }
  }, []);

  const {
    data: adAccountsData,
    isLoading: isAdAccountsLoading,
    isFetching: isAdAccountsFetching,
  } = useGetMetaBuilderAdAccountsQuery(
    { pageId: selectedPageId || "" },
    { skip: !isFacebookConnected || !selectedPageId },
  );

  // Fetch campaigns directly from Meta - ONLY for selected ad account
  const {
    data: metaCampaignsData,
    isLoading: isMetaCampaignsLoading,
    error: metaCampaignsError,
    refetch: refetchMetaCampaigns,
  } = useGetMetaBuilderCampaignsFromMetaQuery(
    { limit: 100, adAccountId: adAccountId || undefined },
    {
      skip: !isFacebookConnected || !adAccountId, // Skip if not connected OR no adAccountId selected
    },
  );

  // Fetch draft campaigns
  const {
    data: draftsData,
    isLoading: isDraftsLoading,
    error: draftsError,
    refetch: refetchDrafts,
  } = useGetDraftCampaignsQuery(
    { adAccountId: adAccountId || undefined },
    {
      skip: !adAccountId, // Skip if no adAccountId selected
    },
  );

  // Status update mutation
  const [updateCampaignStatus, { isLoading: isUpdatingStatus }] =
    useUpdateMetaBuilderCampaignStatusMutation();

  // Duplicate campaign mutation
  const [duplicateCampaign, { isLoading: isDuplicating }] =
    useDuplicateMetaBuilderCampaignMutation();

  // Delete mutations
  const [deleteCampaign] = useDeleteMetaBuilderCampaignMutation();
  const [deleteDraft] = useDeleteDraftCampaignMutation();

  // Lazy query for fetching videos by IDs
  const [getVideosByIds] = useLazyGetVideosByIdsQuery();

  // Save draft mutation
  const [saveDraft] = useSaveDraftCampaignMutation();

  // Redirect to Meta Ads Builder if not connected
  useEffect(() => {
    if (!isAuthStatusLoading && !isFacebookConnected && !hasShownError) {
      setHasShownError(true);
      toast.error("Please connect your Facebook account first");
      navigate(ROUTES.META_ADS_SETUP_PUBLIC);
    }
  }, [isFacebookConnected, isAuthStatusLoading, navigate, hasShownError]);

  const hasMetaCampaignsError = metaCampaignsError != null;
  const metaCampaignsErrorStatus = getRtkQueryStatus(metaCampaignsError);

  // Mobile browsers often hit an expired Meta session on first open. Attempt a silent refresh and one-time refetch.
  useEffect(() => {
    if (!hasMetaCampaignsError) {
      setIsRecoveringMeta(false);
      setHasAutoRetriedMeta(false);
      return;
    }
    if (!isFacebookConnected || !adAccountId) return;
    if (hasAutoRetriedMeta) return;

    const run = async () => {
      setHasAutoRetriedMeta(true);
      setIsRecoveringMeta(true);
      try {
        if (
          metaCampaignsErrorStatus === 401 ||
          metaCampaignsErrorStatus === 403
        ) {
          try {
            await refreshMetaSession().unwrap();
          } catch {
            // Even if refresh fails, still try a refetch once.
          }
        }
        await new Promise((r) => setTimeout(r, 650));
        refetchMetaCampaigns();
      } finally {
        setIsRecoveringMeta(false);
      }
    };

    run();
  }, [
    adAccountId,
    hasMetaCampaignsError,
    hasAutoRetriedMeta,
    isFacebookConnected,
    metaCampaignsErrorStatus,
    refreshMetaSession,
    refetchMetaCampaigns,
  ]);

  // Transform Meta campaigns to frontend format
  const metaCampaigns: CampaignCardData[] = metaCampaignsData?.campaigns
    ? metaCampaignsData.campaigns.map(transformMetaCampaignFromMeta)
    : [];

  // Transform Draft campaigns to frontend format
  const draftCampaigns: CampaignCardData[] = draftsData?.drafts
    ? draftsData.drafts.map(transformDraftCampaign)
    : [];

  // Combine both campaigns (drafts first, then Meta campaigns)
  const campaigns: CampaignCardData[] = [...draftCampaigns, ...metaCampaigns];
  const adAccountOptions: AdAccountOption[] = adAccountsData?.adAccounts || [];
  const selectedAdAccountValue =
    selectedAdAccount?.id || selectedAdAccount?.account_id || "";

  const persistAdAccountSelection = (adAccount: AdAccountOption | null) => {
    try {

      const adAccountIdToSave = adAccount?.id || adAccount?.account_id || "";

      const updatedSelections = {
        adAccount: adAccount
          ? {
              id: adAccountIdToSave,
              name: adAccount.name || "",
            }
          : null,
      };

      const existingAccountData = localStorage.getItem("metaAdsAccountData");
      const parsedAccountData = existingAccountData
        ? JSON.parse(existingAccountData)
        : {};

      const updatedAccountData = {
        ...parsedAccountData,
        adAccount: adAccount
          ? {
              id: adAccountIdToSave,
              name: adAccount.name || "",
            }
          : null,
      };

      localStorage.setItem(
        "metaAdsAccountData",
        JSON.stringify(updatedAccountData),
      );
    } catch (error) {
      console.error("Failed to persist ad account selection:", error);
    }
  };

  // Keep selected ad account details in sync once list loads
  useEffect(() => {
    if (selectedAdAccount || !adAccountId || adAccountOptions.length === 0) {
      return;
    }
    const match = adAccountOptions.find(
      (account) =>
        account.id === adAccountId || account.account_id === adAccountId,
    );
    if (match) {
      setSelectedAdAccount(match);
    }
  }, [adAccountId, adAccountOptions, selectedAdAccount]);

  const handleAdAccountChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    const selectedId = event.target.value;
    if (!selectedId) {
      setSelectedAdAccount(null);
      setAdAccountId(null);
      persistAdAccountSelection(null);
      try {
        localStorage.removeItem(AD_ACCOUNT_STORAGE_KEY);
      } catch (e) {
        console.error("Failed to clear ad account from localStorage:", e);
      }
      return;
    }

    const foundAccount =
      adAccountOptions.find(
        (account) =>
          account.id === selectedId || account.account_id === selectedId,
      ) || null;

    const accountToPersist =
      foundAccount ||
      ({
        id: selectedId,
        name: "",
      } as AdAccountOption);

    setSelectedAdAccount(foundAccount || accountToPersist);
    setAdAccountId(selectedId);
    persistAdAccountSelection(accountToPersist);
    try {
      localStorage.setItem(
        AD_ACCOUNT_STORAGE_KEY,
        JSON.stringify(accountToPersist),
      );
    } catch (e) {
      console.error("Failed to persist ad account to localStorage:", e);
    }
  };

  // Helper function to extract numeric value from engagement string
  const extractEngagementValue = (engagement: string): number => {
    if (!engagement || engagement === "N/A") return 0;

    // Remove currency symbols and extract numeric value
    const numericValue = engagement.replace(/[^\d.]/g, "");
    return parseFloat(numericValue) || 0;
  };

  // Helper function to extract numeric value from cost string
  const extractCostValue = (cost: string): number => {
    if (!cost || cost === "N/A") return 0;

    // Remove currency symbols and extract numeric value
    const numericValue = cost.replace(/[^\d.]/g, "");
    return parseFloat(numericValue) || 0;
  };

  // Helper function to get date for sorting
  const getSortDate = (campaign: CampaignCardData): Date => {
    // Try to use createdAt or updatedAt from raw data
    if (campaign.createdAt) {
      return new Date(campaign.createdAt);
    }
    if (campaign.startDate && campaign.startDate !== "N/A") {
      return new Date(campaign.startDate);
    }
    if (campaign.lastUpdated && campaign.lastUpdated !== "N/A") {
      return new Date(campaign.lastUpdated);
    }
    // Default to old date if no date found
    return new Date(0);
  };

  // Sort campaigns based on selected sort option
  const sortedCampaigns = useMemo(() => {
    const sorted = [...campaigns];

    switch (sortOption) {
      case "Start Date: New to Old":
        return sorted.sort((a, b) => {
          const dateA = getSortDate(a);
          const dateB = getSortDate(b);
          return dateB.getTime() - dateA.getTime();
        });

      case "Start Date: Old to New":
        return sorted.sort((a, b) => {
          const dateA = getSortDate(a);
          const dateB = getSortDate(b);
          return dateA.getTime() - dateB.getTime();
        });

      case "Most Engaged [All Time]":
        return sorted.sort((a, b) => {
          const engagementA = extractEngagementValue(a.engagement);
          const engagementB = extractEngagementValue(b.engagement);
          return engagementB - engagementA;
        });

      case "Lowest cost/Results [All Time]":
        return sorted.sort((a, b) => {
          const costA = extractCostValue(a.cost);
          const costB = extractCostValue(b.cost);
          return costA - costB;
        });

      default:
        return sorted;
    }
  }, [campaigns, sortOption]);

  const handleDuplicate = async (campaignId: string | number) => {
    // Find campaign for the name
    const campaign = campaigns.find((c) => c.id === campaignId);
    if (!campaign) {
      toast.error("Campaign not found");
      return;
    }

    const campaignName = campaign.name;

    try {
      setDuplicatingCampaignId(campaignId.toString());

      // Step 1: Fetch campaign details from Meta
      const campaignResponse = await fetch(
        `${apiBaseUrl}/api/meta-builder/campaigns/${campaignId}/from-meta`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
          },
        },
      );

      if (!campaignResponse.ok) {
        throw new Error("Failed to fetch campaign details");
      }

      const campaignData = await campaignResponse.json();

      // Step 2: Extract video IDs from ads
      const videoIds: string[] = [];
      if (campaignData.ads && Array.isArray(campaignData.ads)) {
        campaignData.ads.forEach((ad: any) => {
          if (ad.creative?.object_story_spec?.video_data?.video_id) {
            videoIds.push(ad.creative.object_story_spec.video_data.video_id);
          }
        });
      }

      // Step 3: Fetch videos by IDs
      let videos: any[] = [];
      if (videoIds.length > 0) {
        try {
          const videosResponse = await getVideosByIds({ videoIds }).unwrap();
          videos = videosResponse.videos || [];
          console.log("Videos API Response:", videosResponse);
        } catch (error) {
          console.error("Error fetching videos:", error);
          // Continue even if videos fail to load
        }
      }

      // Step 4: Download videos and convert to File objects
      const videoFiles: File[] = [];
      console.log("Starting video download, total videos:", videos.length);
      console.log("Videos data:", JSON.stringify(videos, null, 2));
      for (const video of videos) {
        console.log("Processing video:", video);
        console.log("Video keys:", Object.keys(video));
        console.log(
          "Has source:",
          !!video.source,
          "Has permalink_url:",
          !!video.permalink_url,
        );
        try {
          if (video.source || video.permalink_url) {
            const videoUrl = video.source || video.permalink_url;
            console.log("Downloading video from:", videoUrl);
            const response = await fetch(videoUrl);
            const blob = await response.blob();
            const fileName =
              video.title || `video_${video.id || video.video_id}.mp4`;
            const file = new File([blob], fileName, {
              type: blob.type || "video/mp4",
            });
            videoFiles.push(file);
            console.log(
              "Successfully downloaded video:",
              fileName,
              "Size:",
              file.size,
            );
          } else {
            console.warn(
              "Video has no source or permalink_url, skipping:",
              video,
            );
          }
        } catch (error) {
          console.error(
            `Error downloading video ${video.id || video.video_id}:`,
            error,
          );
          // Continue with other videos even if one fails
        }
      }
      console.log("Total video files prepared:", videoFiles.length);

      // Step 5: Prepare draft data
      // Determine budget type and amount
      const dailyBudget = campaignData.campaign.daily_budget
        ? parseFloat(campaignData.campaign.daily_budget.toString()) / 100
        : 0;
      const lifetimeBudget = campaignData.campaign.lifetime_budget
        ? parseFloat(campaignData.campaign.lifetime_budget.toString()) / 100
        : 0;

      // Use daily_budget if available, otherwise lifetime_budget
      const budgetAmount = dailyBudget || lifetimeBudget;
      const budgetType =
        dailyBudget > 0
          ? "DAILY"
          : campaignData.campaign.stop_time
            ? "SCHEDULED"
            : "DAILY";

      console.log("Campaign Data Countries:", campaignData.countries);

      const draftCampaignData = {
        adName: `${campaignName}`,
        adAccountId:
          campaignData.account?.id || campaignData.campaign.account_id,
        pageId:
          campaignData.ads?.[0]?.creative?.object_story_spec?.page_id || "",
        // Use actual Instagram account ID (from API or creative). Never use instagram_positions (e.g. "stream") — that is placement, not account id.
        instagramId:
          campaignData.instagramAccountId ||
          campaignData.ads?.[0]?.creative?.object_story_spec
            ?.instagram_actor_id ||
          (campaignData.ads?.[0]?.creative?.object_story_spec as any)
            ?.instagram_user_id ||
          "",
        pixelId: campaignData.adSets?.[0]?.pixel_id || "",
        campaignBudget: budgetAmount,
        budgetType: budgetType,
        selectedCountries: campaignData.countries || [],
        selectedInterests: (() => {
          const targeting = campaignData.adSets?.[0]?.targeting;
          let interests: any[] = [];

          // Try direct interests first
          if (targeting?.interests && Array.isArray(targeting.interests)) {
            interests = targeting.interests;
          }
          // Try flexible_spec interests
          else if (
            targeting?.flexible_spec &&
            Array.isArray(targeting.flexible_spec)
          ) {
            targeting.flexible_spec.forEach((spec: any) => {
              if (spec.interests && Array.isArray(spec.interests)) {
                interests = [...interests, ...spec.interests];
              }
            });
          }

          return interests.map((i: any) => ({
            id: i.id,
            name: i.name,
          }));
        })(),
        audienceAge:
          campaignData.adSets?.[0]?.targeting?.age_min &&
          campaignData.adSets?.[0]?.targeting?.age_max
            ? `${campaignData.adSets[0].targeting.age_min}-${campaignData.adSets[0].targeting.age_max}`
            : "18-65",
        audienceGender:
          campaignData.adSets?.[0]?.targeting?.genders?.[0] === 1
            ? "Male"
            : campaignData.adSets?.[0]?.targeting?.genders?.[0] === 2
              ? "Female"
              : "All",
        startDate: campaignData.campaign.start_time
          ? new Date(campaignData.campaign.start_time)
              .toISOString()
              .split("T")[0]
          : "",
        startTime: campaignData.campaign.start_time
          ? new Date(campaignData.campaign.start_time)
              .toTimeString()
              .slice(0, 5)
          : "",
        endDate: campaignData.campaign.stop_time
          ? new Date(campaignData.campaign.stop_time)
              .toISOString()
              .split("T")[0]
          : "",
        endTime: campaignData.campaign.stop_time
          ? new Date(campaignData.campaign.stop_time).toTimeString().slice(0, 5)
          : "",
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        isScheduled: campaignData.campaign.stop_time ? "scheduled" : "",
        adHeadline:
          campaignData.ads?.[0]?.creative?.object_story_spec?.video_data
            ?.title || "",
        adText:
          campaignData.ads?.[0]?.creative?.object_story_spec?.video_data
            ?.message || "",
        spotifyUrl:
          campaignData.originalSpotifyUrl || campaignData.linkUrls?.[0] || "",
        artistName: campaignData.originalArtistName ?? "",
        title: campaignData.originalSongName || campaignName,
        adAccountName: campaignData.account?.name || "",
        pageName: "", // Could be fetched from pages API
        instagramName: "", // Could be fetched from instagram API
        pixelName: "", // Could be fetched from pixels API
        campaignStartDate: campaignData.campaign.start_time
          ? new Date(campaignData.campaign.start_time)
              .toISOString()
              .split("T")[0]
          : "",
        campaignEndDate: campaignData.campaign.stop_time
          ? new Date(campaignData.campaign.stop_time)
              .toISOString()
              .split("T")[0]
          : "",
        objective: campaignData.campaign.objective || "",
        campaignName: `${campaignName} - Duplicate`,
        // Save videos in same format as original ad: { id, title }
        duplicatedVideos:
          videos.length > 0
            ? videos.map((v: any) => ({
                id: String(v.id || v.video_id || ""),
                title:
                  v.title ||
                  v.description ||
                  `video_${v.id || v.video_id || "unknown"}.mp4`,
              }))
            : videoIds.map((id: string) => ({ id, title: "" })),
      };

      console.log(
        "Draft Campaign Data:",
        JSON.stringify(draftCampaignData, null, 2),
      );

      // Step 6: Create FormData with campaign data and video files
      const formData = new FormData();
      formData.append("campaignData", JSON.stringify(draftCampaignData));

      // Append video files to FormData
      videoFiles.forEach((file) => {
        formData.append("videos", file);
      });

      console.log(
        "FormData created. Video files to append:",
        videoFiles.length,
      );
      console.log("FormData entries count:", formData.getAll("videos").length);

      // Step 7: Save draft
      await saveDraft(formData).unwrap();

      toast.success(
        `Campaign duplicated as draft: "${campaignName} - Duplicate"${videoFiles.length > 0 ? ` with ${videoFiles.length} video${videoFiles.length > 1 ? "s" : ""}` : ""}`,
      );

      // Refetch drafts to show the new draft
      refetchDrafts();
    } catch (error: any) {
      console.error("Error duplicating campaign to draft:", error);
      toast.error(
        error?.data?.msg ||
          error?.message ||
          "Failed to duplicate campaign. Please try again.",
      );
    } finally {
      setDuplicatingCampaignId(null);
    }
  };

  const handlePause = async (campaignId: string | number) => {
    try {
      setUpdatingCampaignId(campaignId.toString());

      // Find the current campaign to determine its status
      const campaign = campaigns.find((c) => c.id === campaignId);
      if (!campaign) {
        toast.error("Campaign not found");
        return;
      }

      // Determine new status (toggle between ACTIVE and PAUSED)
      const newStatus = campaign.status === "Active" ? "PAUSED" : "ACTIVE";

      // Call the API to update status on Facebook
      await updateCampaignStatus({
        campaignId: campaignId.toString(),
        status: newStatus,
      }).unwrap();

      // Show success message
      const action = newStatus === "ACTIVE" ? "activated" : "paused";
      toast.success(`Campaign ${action} successfully!`);

      // Refetch campaigns to get updated status from Meta
      refetchMetaCampaigns();
    } catch (error: any) {
      console.error("Error updating campaign status:", error);
      toast.error(
        error?.data?.msg ||
          "Failed to update campaign status. Please try again.",
      );
    } finally {
      setUpdatingCampaignId(null);
    }
  };

  const handleDelete = async (
    campaignId: string | number,
    isDraft: boolean,
  ) => {
    // Find campaign name for the modal
    const campaign = campaigns.find((c) => c.id === campaignId);
    if (!campaign) {
      toast.error("Campaign not found");
      return;
    }

    // Show confirmation modal
    setDeleteModal({
      isOpen: true,
      campaignId,
      campaignName: campaign.name,
      isDraft,
    });
  };

  const handleDeleteConfirm = async () => {
    const { campaignId, isDraft } = deleteModal;

    try {
      setDeletingCampaignId(campaignId.toString());

      if (isDraft) {
        // Delete draft campaign
        await deleteDraft(campaignId.toString()).unwrap();
        toast.success("Draft deleted successfully!");
        refetchDrafts();
      } else {
        // Delete campaign from Meta
        await deleteCampaign(campaignId.toString()).unwrap();
        toast.success("Campaign deleted successfully from Meta!");
        refetchMetaCampaigns();
      }

      // Close modal after successful deletion
      setDeleteModal({
        isOpen: false,
        campaignId: "",
        campaignName: "",
        isDraft: false,
      });
    } catch (error: any) {
      console.error("Error deleting:", error);
      toast.error(
        error?.data?.msg ||
          error?.data?.error ||
          `Failed to delete ${isDraft ? "draft" : "campaign"}. Please try again.`,
      );
    } finally {
      setDeletingCampaignId(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModal({
      isOpen: false,
      campaignId: "",
      campaignName: "",
      isDraft: false,
    });
  };

  const handleEdit = async (campaignId: string | number) => {
    // Show confirmation modal
    setEditModal({
      isOpen: true,
      campaignId,
    });
  };

  const handleEditConfirm = () => {
    const { campaignId } = editModal;

    // Close modal and navigate to edit page
    setEditModal({
      isOpen: false,
      campaignId: "",
    });

    // Navigate to edit page with campaignId
    navigate(`/create-meta-ads?campaignId=${campaignId}`);
  };

  const handleEditCancel = () => {
    setEditModal({
      isOpen: false,
      campaignId: "",
    });
  };

  const isBlockingInitialLoad =
    isAuthStatusLoading || isRecoveringMeta || isMetaCampaignsLoading || isDraftsLoading;

  // Show error state
  if (metaCampaignsError) {
    return (
      <div className="w-full min-h-screen flex items-center justify-center bg-black">
        <div className="text-center">
          <p className="text-red-400 mb-4">
            Error loading campaigns from Meta. Please try again.
          </p>
          <button
            onClick={() => refetchMetaCampaigns()}
            className="px-6 py-2 bg-[#58F2A9] rounded-full text-black font-semibold"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {isBlockingInitialLoad && (
        <div className="fixed inset-0 z-[9999] min-h-screen flex items-center justify-center backdrop-blur-[15px] bg-[rgba(0,0,0,0.45)]">
          <CircularProgress size={50} sx={{ color: "#58F2A9" }} />
        </div>
      )}
      <div className="relative md:min-h-screen xl:inline-block xl:w-[calc(100%-320px)] overflow-x-hidden">
        <div
          className="relative sidebar-wrapper pt-[40px] pb-[24px] md:pb-[40px] lg:pl-[32px] w-full"
          style={{ maxWidth: "1400px", zIndex: 1 }}
        >
          <div className="flex flex-col items-center gap-6 w-full">
            {/* Header + nav grouped with smaller gap between description and nav — aligned with meta-ads-builder-public */}
            <MetaAdsHeaderNav
              activeAudienceTab={activeTab}
              onAudienceTabChange={handleAudienceTabChange}
              activeNavItem="myCampaigns"
              onNewCampaignClick={() => navigate(ROUTES.META_ADS_BUILDER)}
              onSmartLinksClick={() => navigate(ROUTES.SMART_LINKS)}
            />

            {/* Mobile divider between tabs and card */}
            {isFacebookConnected && (
              <div className="block md:hidden w-full h-px bg-[#202027] mt-[-25px] mb-[15px]" />
            )}

            <div className="mt-[-16px] flex flex-col items-center px-5 pt-6 pb-8 sm:px-6 sm:pt-8 sm:pb-10 lg:px-16 lg:pt-16 lg:pb-16 relative self-stretch w-full flex-[0_0_auto] bg-[#0e0e10] rounded-[24px] md:rounded-[40px] overflow-hidden border-[none] shadow-[0px_10px_40px_#060c1180] before:content-[''] before:absolute before:inset-0 before:p-0.5 before:rounded-[24px] md:before:rounded-[40px] before:[background:linear-gradient(0deg,rgba(255,255,255,1)_0%,rgba(0,26,255,1)_45%,rgba(27,118,255,1)_100%)] before:[-webkit-mask:linear-gradient(#fff_0_0)_content-box,linear-gradient(#fff_0_0)] before:[-webkit-mask-composite:xor] before:[mask-composite:exclude] before:z-[1] before:pointer-events-none">
              <div className="absolute top-0 left-0 w-full h-full rounded-[24px] md:rounded-[40px] overflow-hidden">
                <div className="relative top-[-100px] lg:top-[-285px] left-1/2 transform -translate-x-1/2 w-[90vw] max-w-full lg:w-[1409px] h-[200px] lg:h-[452px] rounded-full blur-[110px] bg-[linear-gradient(270deg,rgba(182,211,255,1)_0%,rgba(0,34,255,1)_53%,rgba(34,255,244,1)_100%)]" />
              </div>

              <div className="flex flex-col w-full items-start justify-center gap-6 md:gap-8 relative flex-[0_0_auto] min-w-0 ">
                {isBlockingInitialLoad ? (
                  <div className="w-full py-14 flex items-center justify-center">
                    <CircularProgress sx={{ color: "#58F2A9" }} />
                  </div>
                ) : adAccountId ? (
                  <>
                    <div className="flex flex-col items-start w-full pt-2 md:pt-0">
                      <div className="w-full rounded-3xl">
                        <div
                          className="flex flex-col lg:flex-row 
                  lg:items-center lg:justify-between 
                  gap-6 w-full"
                        >
                          {/* LEFT SIDE CONTENT */}
                          <div className="flex flex-col gap-3 md:gap-4 max-w-[900px]">
                            {/* HEADING */}
                            <p
                              className="text-white 
                    text-[24px] lg:text-[32px] 
                    font-semibold leading-[105%]"
                            >
                              Your{" "}
                              {campaigns.length > 1 ? "campaigns" : "campaign"}{" "}
                              {campaigns.length > 1 ? "are" : "is"} listed here
                            </p>

                            {/* DESCRIPTION */}
                            <p
                              className="text-white/80 
                    text-[16px]  lg:text-[20px] 
                    font-medium"
                            >
                              See how your tracks are performing across{" "}
                              {campaigns.length > 1 ? "campaigns" : "campaign"}
                            </p>
                          </div>

                          {/* RIGHT SIDE BUTTON */}
                          {(() => {
                            // Check if pixel exists in localStorage
                            const hasPixel = (() => {
                              try {
                                const accountData = localStorage.getItem("metaAdsAccountData");
                                if (!accountData) return false;
                                const parsedData = JSON.parse(accountData);
                                return !!parsedData?.pixel?.id;
                              } catch (e) {
                                return false;
                              }
                            })();

                            if (!hasPixel) return null;

                            return (
                              <div className="w-full lg:w-auto">
                                <button
                                  className="w-full lg:w-auto
                       inline-flex justify-center items-center
                       px-6 py-3 rounded-full
                       font-semibold text-white text-base
                       transition-all duration-300"
                                  style={{
                                    background:
                                      "linear-gradient(to right,  #1C3941, #58F2A9)",
                                    border: "1px solid #58F2A9",
                                  }}
                                  onClick={() =>
                                    navigate(ROUTES.META_NEW_COMPAING_PUBLIC)
                                  }
                                >
                                  Create new Campaign
                                </button>
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    </div>

                    <img
                      className="relative w-full h-px object-cover"
                      alt=""
                      src={line3}
                    />

                    <section className="flex flex-col items-start gap-4 relative self-stretch w-full flex-[0_0_auto] pt-1 md:pt-0">
                      <div
                        className="w-full flex flex-col lg:flex-row 
                lg:items-center lg:justify-between 
                gap-6"
                      >
                        {/* LEFT SIDE HEADING */}
                        <h2
                          className="text-white 
                 text-[22px] md:text-[28px] lg:text-[32px] 
                 font-semibold opacity-90"
                        >
                          My {campaigns.length > 1 ? "Campaigns" : "Campaign"}
                        </h2>

                        {/* RIGHT SIDE TABS */}
                        <div className="w-full lg:w-auto">
                          <div
                            className="flex gap-[8px] w-full lg:w-[262px] 
               h-[50px]
               bg-[#2e3244] 
               p-[8px] 
               rounded-full items-center"
                          >
                            {/* TRACKS BUTTON */}
                            <button
                              onClick={() => handlePromoteTabChange("tracks")}
                              className={`flex-1 h-full
                 rounded-full 
                 text-[16px] font-medium 
                 
        ${
          tracksTabState === "tracks"
            ? "bg-white text-black shadow-md"
            : "text-white "
        }`}
                            >
                              Tracks
                            </button>
                            {/* PLAYLISTS BUTTON */}
                            <button
                              onClick={() =>
                                handlePromoteTabChange("playlists")
                              }
                              className={`flex-1 h-full
                 rounded-full 
                 text-[16px] font-medium 
                 
        ${
          tracksTabState === "playlists"
            ? "bg-white text-black shadow-md"
            : "text-white"
        }`}
                            >
                              Playlists
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* SEARCH AND SORT BAR */}
                      <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between w-full mt-2 md:mt-6 gap-3 md:gap-4 relative">
                        {/* SEARCH BAR - LEFT SIDE */}
                        <div className="order-2 md:order-1 w-full md:flex-1 relative mb-0 md:mb-[20px]">
                          <svg
                            className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#878da1]"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                            />
                          </svg>
                          <input
                            type="text"
                            placeholder="Search campaigns..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full md:w-[339px] h-[48px] pl-12 pr-4 rounded-[10px] bg-[#1A1D24] text-white text-base placeholder:text-[#878da1] focus:outline-none focus:ring-2 focus:ring-[#58F2A9]"
                          />
                        </div>

                        {/* SORT SELECTOR - RIGHT SIDE */}
                        <div className="order-1 md:order-2 w-full md:w-auto flex justify-end md:block">
                          <SortSelector
                            selectedSort={sortOption}
                            onSortChange={setSortOption}
                          />
                        </div>
                      </div>

                      {/* CAMPAIGNS LIST */}
                      <div className="flex flex-col gap-6 relative self-stretch w-full">
                        {/* Filter and sort campaigns based on search query */}
                        {(() => {
                          const tabFilteredCampaigns = sortedCampaigns.filter(
                            (campaign) => {
                              const campaignType =
                                campaign.promoteType ||
                                inferPromoteTypeFromUrl(campaign.linkUrls?.[0]);
                              return tracksTabState === "playlists"
                                ? campaignType === "playlist"
                                : campaignType === "track";
                            },
                          );
                          const filteredCampaigns = tabFilteredCampaigns.filter(
                            (campaign) =>
                              campaign.name
                                .toLowerCase()
                                .includes(searchQuery.toLowerCase()),
                          );

                          // Show "No results found" when no campaigns match search
                          if (filteredCampaigns.length === 0) {
                            return (
                              <div className="text-center text-[#878da1] text-lg py-12">
                                {searchQuery
                                  ? "No results found"
                                  : `No ${tracksTabState === "playlists" ? "playlist" : "track"} campaigns found`}
                              </div>
                            );
                          }

                          // Show filtered and sorted campaigns
                          return filteredCampaigns.map((campaign, index) => (
                            <MetaBuilderCampaignCard
                              key={campaign.id}
                              campaign={campaign}
                              index={index}
                              onDuplicate={handleDuplicate}
                              onPause={handlePause}
                              onDelete={handleDelete}
                              onEdit={handleEdit}
                              isUpdating={updatingCampaignId === campaign.id}
                              isDuplicating={
                                duplicatingCampaignId === campaign.id
                              }
                              isDeleting={deletingCampaignId === campaign.id}
                            />
                          ));
                        })()}
                      </div>
                    </section>
                  </>
                ) : (
                  <div className="flex flex-col w-full gap-6 md:gap-8">
                    <div className="flex flex-col gap-3 md:gap-4 max-w-[900px] pt-2 md:pt-0">
                      <p className="text-white text-[24px] lg:text-[32px] font-semibold leading-[110%]">
                        Ad Account not selected
                      </p>
                      <p className="text-white/80 text-[16px] lg:text-[20px] font-medium">
                        You haven&apos;t selected the ad account from which you
                        ran the campaign to display it!
                      </p>
                      <div className="w-full h-px bg-white/10 mt-1 md:mt-2" />
                    </div>

                    <div className="flex flex-col gap-3 w-full max-w-[460px]">
                      <label className="text-white text-[14px] font-medium">
                        Choose your Ad Account
                      </label>
                      <div className="relative w-full">
                        <select
                          value={selectedAdAccountValue}
                          onChange={handleAdAccountChange}
                          disabled={
                            isAdAccountsLoading ||
                            isAdAccountsFetching ||
                            adAccountOptions.length === 0
                          }
                          className="w-full h-[48px] bg-white text-black rounded-[10px] pl-4 pr-11 text-[11px] font-medium focus:outline-none focus:ring-2 focus:ring-[#58F2A9] appearance-none"
                        >
                          <option value="">
                            {isAdAccountsLoading || isAdAccountsFetching
                              ? "Loading ad accounts..."
                              : adAccountOptions.length === 0
                                ? "No ad accounts found"
                                : "Select an ad account"}
                          </option>
                          {adAccountOptions.map((account) => {
                            const optionValue =
                              account.id || account.account_id || "";
                            return (
                              <option key={optionValue} value={optionValue}>
                                {account.name}
                                {account.account_id
                                  ? ` [${account.account_id}]`
                                  : ""}
                              </option>
                            );
                          })}
                        </select>

                        {adAccountOptions.length > 0 &&
                          !isAdAccountsLoading &&
                          !isAdAccountsFetching && (
                            <img
                              className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-[22.4px] h-[22.4px]"
                              src={agnleDownSmall}
                              alt=""
                            />
                          )}
                      </div>
                      <p className="text-[#878da1] text-[10px]">
                        Make sure it has permissions to your Page
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={deleteModal.isOpen}
        campaignName={deleteModal.campaignName}
        isDraft={deleteModal.isDraft}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        isDeleting={deletingCampaignId === deleteModal.campaignId.toString()}
      />

      {/* Edit Confirmation Modal */}
      <EditConfirmationModal
        isOpen={editModal.isOpen}
        onConfirm={handleEditConfirm}
        onCancel={handleEditCancel}
      />
    </>
  );
}
